import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Alert, Skeleton, Table, TableBody,
  TableCell, TableHead, TableRow, LinearProgress,
} from '@mui/material';
import { fetchIntegrityReport, IntegrityReport } from '../../services/proctoringService';

const bandColour = (band: string): 'success' | 'warning' | 'error' | 'default' => {
  const b = (band || '').toLowerCase();
  if (b.includes('clear') || b.includes('clean')) return 'success';
  if (b.includes('review') || b.includes('minor')) return 'warning';
  if (b.includes('flag') || b.includes('concern')) return 'error';
  return 'default';
};

/**
 * Integrity report for one sitting (#06).
 *
 * The synthetic-data banner is not decoration. Every proctor event on this estate is
 * currently seeded, and a report that looks observed would let a teacher act on a
 * student over evidence that was manufactured for testing. The API says so in the
 * payload; this makes sure it is impossible to miss on screen.
 */
const IntegrityPanel: React.FC<{ sessionId: string; studentName?: string }> = ({ sessionId, studentName }) => {
  const [report, setReport] = useState<IntegrityReport | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'locked' | 'forbidden' | 'error'>('loading');
  const [message, setMessage] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchIntegrityReport(sessionId);
      if (cancelled) return;
      if (result.state === 'ready') { setReport(result.report); setState('ready'); }
      else { setState(result.state); setMessage(result.message); }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (state === 'loading') return <Skeleton variant="rounded" height={200} />;
  if (state === 'locked') {
    return <Alert severity="info">Integrity reports are part of the proctoring plan.</Alert>;
  }
  if (state === 'forbidden') {
    return <Alert severity="warning">You do not have permission to view integrity reports.</Alert>;
  }
  if (state === 'error' || !report) {
    return <Alert severity="warning">{message || 'The integrity report could not be loaded.'}</Alert>;
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {report.studentName || studentName || 'Sitting'}
          </Typography>
          <Chip size="small" color={bandColour(report.band)} label={report.band} />
          <Typography variant="body2" color="text.secondary">
            {report.eventCount} event{report.eventCount === 1 ? '' : 's'}
          </Typography>
        </Box>

        {report.syntheticData && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This report is built on <strong>seeded</strong> proctoring events, not observed
            behaviour. Do not act on it as evidence about a student.
            {report.note ? ` ${report.note}` : ''}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">Integrity score</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, report.integrityScore))}
            sx={{ flex: 1, height: 8, borderRadius: 4 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{report.integrityScore}</Typography>
        </Box>

        {report.byType.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell align="right">Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.byType.map((row) => (
                <TableRow key={`${row.type}-${row.severity}`}>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined"
                          color={row.severity === 'HIGH' ? 'error' : row.severity === 'MEDIUM' ? 'warning' : 'default'}
                          label={row.severity} />
                  </TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          // Said explicitly: an empty table could read as "we did not look".
          <Typography variant="body2" color="text.secondary">
            Nothing was flagged during this sitting.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default IntegrityPanel;
